import '../../atoms/buttons/enums.js';
import { getLang } from '../dom-helpers/index.js';

class PredictiveSearchService {
    constructor(config) {
        this.abortController = null;
        this.debounceTimer = null;
        this.debounceDelay = 300; // milliseconds
        this.config = config;
    }
    /**
     * Update service configuration
     */
    updateConfig(config) {
        this.config = Object.assign(Object.assign({}, this.config), config);
    }
    /**
     * Perform a debounced search
     * Returns a promise that resolves with the full API response
     */
    async debouncedSearch(term) {
        return new Promise((resolve, reject) => {
            // Clear any pending debounced timer
            this.cancelDebounce();
            // Abort any ongoing request
            this.abort();
            if (!term.trim()) {
                resolve({
                    autoCompleteSuggestions: [],
                    productResult: {
                        items: [],
                        total: 0,
                        count: 0,
                        offset: 0,
                        hasMore: false,
                    },
                    contentResult: {
                        items: [],
                        total: 0,
                        count: 0,
                        offset: 0,
                        hasMore: false,
                    },
                });
                return;
            }
            // Set up a new debounced call
            this.debounceTimer = setTimeout(async () => {
                try {
                    const results = await this.search(term);
                    resolve(results);
                }
                catch (error) {
                    reject(error);
                }
            }, this.debounceDelay);
        });
    }
    /**
     * Perform an immediate search without debouncing
     * Returns the full API response with products, content, and autocomplete suggestions
     */
    async search(term) {
        var _a;
        if (!term.trim()) {
            return {
                autoCompleteSuggestions: [],
                productResult: {
                    items: [],
                    total: 0,
                    count: 0,
                    offset: 0,
                    hasMore: false,
                },
                contentResult: {
                    items: [],
                    total: 0,
                    count: 0,
                    offset: 0,
                    hasMore: false,
                },
            };
        }
        // Cancel any previous request
        this.abort();
        // Create a new abort controller
        this.abortController = new AbortController();
        const requestBody = {
            Term: term.trim(),
            Offset: 0,
            Size: this.config.maxResults,
            Language: (_a = getLang()) !== null && _a !== void 0 ? _a : 'en-us',
            Categories: this.config.categories,
        };
        try {
            const response = await fetch(this.config.apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
                signal: this.abortController.signal,
            });
            if (!response.ok) {
                throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            // Return the full response
            return data;
        }
        catch (error) {
            // Don't throw error if request was aborted (user is still typing)
            if (error.name === 'AbortError') {
                return {
                    autoCompleteSuggestions: [],
                    productResult: {
                        items: [],
                        total: 0,
                        count: 0,
                        offset: 0,
                        hasMore: false,
                    },
                    contentResult: {
                        items: [],
                        total: 0,
                        count: 0,
                        offset: 0,
                        hasMore: false,
                    },
                };
            }
            throw error;
        }
    }
    /**
     * Abort any ongoing fetch request
     */
    abort() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }
    /**
     * Cancel any pending debounced timer
     */
    cancelDebounce() {
        if (this.debounceTimer !== null) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    }
    /**
     * Cleanup resources
     * Call this when the service is no longer needed
     */
    destroy() {
        this.cancelDebounce();
        this.abort();
    }
}

export { PredictiveSearchService };
